import { createRouter, createWebHistory } from 'vue-router'
import { getAuthUser, isAuthValid, reportPageView } from '../auth'

const routes = [
  { path: '/chat', name: 'Communication', component: () => import('../views/CommunicationPage.vue') },
  {
    path: '/launcher-migrate',
    name: 'LauncherMigration',
    component: () => import('../views/LauncherMigration.vue'),
    meta: { publicPage: true }
  },
  {
    path: '/',
    name: 'OfficialHome',
    component: () => import('../views/OfficialHome.vue'),
    meta: { publicPage: true }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue')
  },
  {
    path: '/app',
    name: 'AppLogin',
    component: () => import('../views/Login.vue'),
    meta: { loginPage: true }
  },
  {
    path: '/goods',
    name: 'Goods',
    component: () => import('../views/ProductTabs.vue')
  },
  {
    path: '/categories',
    name: 'Categories',
    redirect: '/goods?tab=categories'
  },
  {
    path: '/attributes',
    name: 'Attributes',
    redirect: '/goods?tab=attributes'
  },
  {
    path: '/suppliers',
    name: 'Suppliers',
    redirect: '/goods?tab=suppliers'
  },
  {
    path: '/flows',
    name: 'Flows',
    component: () => import('../views/Flows.vue')
  },
  {
    path: '/ordering',
    name: 'Ordering',
    component: () => import('../views/Ordering.vue')
  },
  {
    path: '/purchase',
    name: 'Purchase',
    component: () => import('../views/PurchaseFlow.vue')
  },
  {
    path: '/salary',
    name: 'Salary',
    component: () => import('../views/Salary.vue')
  },
  {
    path: '/permissions',
    name: 'Permissions',
    component: () => import('../views/Permissions.vue')
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('../views/Settings.vue')
  },
  {
    path: '/settings/launcher',
    name: 'LauncherConfigSettings',
    component: () => import('../views/LauncherConfigSettings.vue')
  },
  {
    path: '/web-pos',
    name: 'WebPos',
    component: () => import('../views/WebPos.vue')
  },
  {
    path: '/receipts',
    name: 'Receipts',
    component: () => import('../views/Receipts.vue')
  },
  {
    path: '/receipt-settings',
    name: 'ReceiptSettings',
    component: () => import('../views/ReceiptSettings.vue')
  },
  {
    path: '/downloads',
    name: 'DownloadCenter',
    component: () => import('../views/DownloadCenter.vue')
  },
  {
    path: '/members',
    name: 'Members',
    component: () => import('../views/Members.vue')
  },
  {
    path: '/promotions',
    name: 'Promotions',
    component: () => import('../views/Promotions.vue')
  },
  {
    path: '/team',
    name: 'Team',
    component: () => import('../views/Team.vue')
  },
  {
    path: '/reports',
    name: 'Reports',
    component: () => import('../views/Reports.vue')
  },
  {
    path: '/analysis',
    name: 'Analysis',
    component: () => import('../views/Analysis.vue')
  },
  {
    path: '/followup',
    name: 'FollowUp',
    component: () => import('../views/FollowUp.vue')
  },
  {
    path: '/invoices',
    name: 'Invoices',
    component: () => import('../views/InvoiceRequests.vue')
  },
  {
    path: '/operations',
    name: 'Operations',
    component: () => import('../views/Operations.vue')
  },
  { path: '/operation', redirect: '/operations' },
  {
    path: '/warehouse',
    name: 'Warehouse',
    component: () => import('../views/Warehouse.vue')
  },
  {
    path: '/stocktake',
    name: 'Stocktake',
    component: () => import('../views/Stocktake.vue')
  },
  { path: '/daily', redirect: '/operations' },
  { path: '/vip-login', name: 'VipLogin', component: () => import('../views/VipLogin.vue'), meta: { vipPublic: true } },
  { path: '/vip-card', alias: '/vip-cards', name: 'VipCard', component: () => import('../views/VipCard.vue'), meta: { vipPublic: true } },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue')
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

const ADMIN_ROLES = new Set(['admin', 'highadmin', 'superadmin'])
const ADMIN_ONLY_PATHS = ['/goods', '/categories', '/attributes', '/suppliers', '/promotions', '/analysis', '/stocktake', '/ordering']
const SUPERADMIN_ONLY_PATHS = ['/permissions', '/settings']
const POS_ROLES = new Set(['store', 'admin', 'highadmin', 'superadmin'])
const TEAM_ROLES = new Set(['store', 'manager', 'admin', 'highadmin', 'superadmin'])
const FOLLOWUP_ROLES = new Set(['store', 'manager', 'admin', 'highadmin', 'superadmin'])
const INVOICE_ROLES = new Set(['manager', 'admin', 'highadmin', 'superadmin'])
const WAREHOUSE_ROLES = new Set(['warehouse', 'superadmin'])
const SALARY_ROLES = new Set(['manager', 'admin', 'highadmin', 'superadmin'])
const PAGE_TITLES = {
  '/chat': '沟通',
  '/operations': '运营',
  '/warehouse': '仓库',
  '/stocktake': '盘点',
  '/web-pos': 'POS',
  '/members': '会员',
  '/team': '团队',
  '/reports': '报表',
  '/analysis': '分析',
  '/followup': '跟进',
  '/invoices': '开票',
  '/receipts': '单据',
  '/receipt-settings': '小票设置',
  '/downloads': '下载中心',
  '/flows': '货流',
  '/ordering': '订货',
  '/purchase': '进货',
  '/goods': '商品',
  '/promotions': '促销',
  '/salary': '薪资',
  '/permissions': '权限管理',
  '/settings': '系统设置',
}

const currentRole = () => String(getAuthUser()?.role || localStorage.getItem('role') || '').trim().toLowerCase()

const canAccessPath = (path, role) => {
  if (role === 'chat_only') return path === '/chat' || path.startsWith('/chat/')
  if (role === 'picker') {
    return path === '/operations' || path === '/daily' || path === '/chat'
  }
  if (SUPERADMIN_ONLY_PATHS.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return role === 'superadmin'
  }
  if (path === '/web-pos' || path.startsWith('/web-pos/')) {
    return POS_ROLES.has(role)
  }
  if (path === '/team' || path.startsWith('/team/')) {
    return TEAM_ROLES.has(role)
  }
  if (path === '/followup' || path.startsWith('/followup/')) {
    return FOLLOWUP_ROLES.has(role)
  }
  if (path === '/invoices' || path.startsWith('/invoices/')) {
    return INVOICE_ROLES.has(role)
  }
  if (path === '/warehouse' || path.startsWith('/warehouse/')) {
    return WAREHOUSE_ROLES.has(role)
  }
  if (path === '/salary' || path.startsWith('/salary/')) {
    return SALARY_ROLES.has(role)
  }
  if (ADMIN_ONLY_PATHS.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return ADMIN_ROLES.has(role)
  }
  return true
}

router.beforeEach((to, from, next) => {
  if (to.meta?.vipPublic || to.meta?.publicPage) {
    next()
    return
  }
  if (to.path === '/login' || to.meta?.loginPage) {
    if (isAuthValid()) {
      const redirect = String(to.query.redirect || '').trim()
      if (redirect && redirect.startsWith('/')) {
        next(redirect)
        return
      }
      next(currentRole() === 'chat_only' ? '/chat' : '/operations')
      return
    }
    next()
    return
  }
  if (!isAuthValid()) {
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }
  const role = currentRole()
  if (!canAccessPath(to.path, role)) {
    next(role === 'chat_only' ? '/chat' : '/operations')
    return
  }
  next()
})

router.afterEach((to) => {
  if (to.meta?.vipPublic || to.path === '/login' || !isAuthValid()) return
  reportPageView({
    path: to.path,
    title: PAGE_TITLES[to.path] || String(to.name || to.path || ''),
  })
})

export default router 
